/**
 * The popup's right side (screenshots/72, 76–79): "Live preview" with S/M/L, table view and a wider view, the real widget
 * (a ChartWidget fed the effective settings, so it is exactly what lands on the overview), what each tab adds under it, and
 * the query footer (rows shown, query time, updated at, the money caveat).
 *
 * In-place edits (81 #5): click the title or a bar label to edit it right there; the Labels tab opens with that field pulsing.
 */
import { useState } from "react";
import { CIcon } from "../icons";
import { ChartTable, ChartWidget } from "../ChartWidget";
import { DATASETS, fillTemplate, tableFor, type ChartSettings } from "../chartSettings";
import { CHART_TYPES, sizePlan, type Shape, type WidgetSize } from "../chartTypes";
import type { SettingsTab } from "../chartSettingsStore";
import type { ChartTheme } from "../themes";
import type { DatasetResult } from "../../types";
import { Seg } from "./ui";
import { AlsoFiltering, LabelChangesCard, MotionTimeline, OtherSizes, QueryResults, VisionStrip, type Change } from "./PreviewExtras";

type Vars = Record<string, Record<string, string | number>>;
/** "Sample data" only when the rows are sample (no producer yet); real rows show their source and caveats. */
export const sourceOf = (r: DatasetResult | null | undefined, s: ChartSettings) => (r?.sample || (!r && DATASETS[s.data.datasetId].sample)
  ? { kind: "sample" as const } : { kind: "real" as const, text: r?.caveats.length ? r.caveats.join(" ") : DATASETS[s.data.datasetId].source });
export const CELLS: Record<WidgetSize, [number, number]> = { S: [3, 2], M: [6, 3], L: [8, 4] };
const timeOf = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "");

export interface PreviewProps {
  tab: SettingsTab;
  settings: ChartSettings;          // effective (hover previews included)
  committed: ChartSettings;
  shape: Shape;
  result: DatasetResult | null;
  loading?: boolean;
  vars?: Vars;
  theme?: ChartTheme;
  dropHint: boolean;                // a held field the Split slot takes (76 "Release to split bars")
  changes: Change[];
  crossTargets: { title: string; icon: string; type: string }[];
  onTitle: (v: string) => void;
  onRename: (label: string, v: string) => void;
  onPulse: (field: string) => void;
  wide: boolean;
  onWide: () => void;
}

export function PreviewPane(p: PreviewProps) {
  const { settings: s, shape, result } = p;
  const [size, setSize] = useState<WidgetSize>("L");
  const [asTable, setAsTable] = useState(false);
  const [replay, setReplay] = useState(0);
  const [sel, setSel] = useState<{ key: string; label: string } | null>(null);
  const ds = DATASETS[s.data.datasetId];
  const rowsShown = shape.kind === "category" ? Math.min(shape.rows.length, sizePlan(s.type, "L").maxRows) : result?.rows.length ?? 0;
  const money = ds.id === "contributions";
  const total = result?.total ?? rowsShown;
  const labelOf = (key: string) => (shape.kind === "category" || shape.kind === "signed" ? shape.rows.find((r) => r.key === key)?.label : undefined) ?? key;

  return (
    <section className={`chart-settings__preview cs-tab-${p.tab}`} aria-label="Preview">
      <div className="chart-settings__toolbar">
        <span className={`chip ${ds.live ? "chip--positive" : "chip--neutral"}`}>{ds.live && <span className="dot dot--live" />}{ds.live ? ds.label : `Snapshot · ${ds.label}`}</span>
        <span className="spacer" />
        <Seg label="Preview size" value={size} onChange={setSize} options={[{ id: "S", label: "S" }, { id: "M", label: "M" }, { id: "L", label: "L" }]} />
        <button type="button" className="icon-btn" aria-label="Show as table" aria-pressed={asTable} onClick={() => setAsTable((v) => !v)}><CIcon name="table" size={16} /></button>
        <button type="button" className="icon-btn" aria-label={p.wide ? "Back to the settings" : "Wider preview"} aria-pressed={p.wide} onClick={p.onWide}><CIcon name="expand" size={16} /></button>
      </div>
      <div className={`chart-settings__stage${p.loading ? " is-loading" : ""}`}>
        <WidgetCard {...p} size={size} asTable={asTable} replay={replay} sel={sel}
          onSelect={(key) => { if (s.interact.click === "filter") setSel(sel?.key === key ? null : { key, label: labelOf(key) }); }} onClearSel={() => setSel(null)} />
        {p.tab === "chart" && <OtherSizes size={size} render={(z) => <WidgetCard {...p} size={z} asTable={false} replay={0} sel={null} small />} />}
        {p.tab === "data" && result && <QueryResults result={result} settings={s} onOpenFull={() => setAsTable(true)} />}
        {p.tab === "labels" && p.changes.length > 0 && <LabelChangesCard changes={p.changes} />}
        {p.tab === "colors" && <VisionStrip settings={s} shape={shape} theme={p.theme} />}
        {p.tab === "interact" && <>{s.interact.click === "filter" && p.crossTargets.length > 0 && <AlsoFiltering targets={p.crossTargets} sel={sel} />}
          <MotionTimeline settings={s} shape={shape} theme={p.theme} onReplay={() => setReplay((n) => n + 1)} /></>}
      </div>
      <footer className="cs-stats">
        <span><CIcon name="table" size={12} />{rowsShown} rows shown{money && s.data.mapping.category === "recipient" ? ` · ${total} alderpersons funded` : ""}</span>
        {result && <span><CIcon name="timer" size={12} />Query {result.queryMs} ms</span>}
        {result && <span><CIcon name="refresh-cw" size={12} />Updated {timeOf(result.updatedAt)}</span>}
        <span className="spacer" />
        {money && <span>Money near a vote isn’t evidence of influence.</span>}
      </footer>
    </section>
  );
}

interface CardProps extends PreviewProps {
  size: WidgetSize; asTable: boolean; replay: number; small?: boolean;
  sel: { key: string; label: string } | null;
  onSelect?: (key: string) => void; onClearSel?: () => void;
}

/** The widget as it will look on the overview: header (icon · title · subtitle · ⋯) and the chart. */
function WidgetCard(p: CardProps) {
  const { settings: s, shape, size } = p;
  const [editTitle, setEditTitle] = useState(false);
  const [editLabel, setEditLabel] = useState<{ label: string; x: number; y: number } | null>(null);
  const vars0 = { client: p.result?.totals?.name ?? "", since: "2019", sources: "all sources (incl. lobbyists)", updated: timeOf(p.result?.updatedAt) };
  const title = fillTemplate(s.labels.title ?? CHART_TYPES[s.type].label, vars0);
  const sub = s.labels.subtitle ? fillTemplate(s.labels.subtitle, vars0) : undefined;
  const [w, h] = CELLS[size];
  return (
    <div className={`cs-card cs-card--${size}${p.small ? " is-small" : ""}`} data-size={size}>
      <div className="cs-card__head">
        <CIcon name={CHART_TYPES[s.type].icon} size={15} />
        <div className={`cs-card__titles${editTitle ? " is-editing" : ""}`}>
          {editTitle ? (
            <input className="cs-card__title-input" aria-label="Chart title" autoFocus defaultValue={s.labels.title ?? ""}
              onBlur={(e) => { setEditTitle(false); if (e.target.value.trim() && e.target.value !== s.labels.title) p.onTitle(e.target.value.trim()); }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { e.preventDefault(); setEditTitle(false); } }} />
          ) : p.small ? <strong className="cs-card__title">{size === "S" ? title.split(" by ")[0] : title}</strong> : (
            <button type="button" className="cs-card__title" aria-label={`Title: ${title}. Edit in place`} onClick={() => { setEditTitle(true); p.onPulse("title"); }}>{title}</button>
          )}
          {sub && size !== "S" && <small>{sub}</small>}
          {!p.small && p.sel && <span className="cs-filtering"><CIcon name="funnel" size={11} />Filtering overview: {p.sel.label}
            <button type="button" aria-label="Clear the filter" onClick={p.onClearSel}><CIcon name="x" size={11} /></button></span>}
        </div>
        <CIcon name="ellipsis" className="cs-card__menu" />
      </div>
      <div className="cs-card__body">
        {p.asTable ? <ChartTable shape={shape} maxRows={sizePlan(s.type, size).maxRows} /> : (
          <ChartWidget key={p.replay} type={s.type} shape={shape} w={w} h={h} title={title} settings={s.type === "table" ? { ...s, table: tableFor(s) } : s}
            allRowsTotal={p.result?.totals ? p.result.totals.company + p.result.totals.lobbyists : undefined} theme={p.theme} vars={p.vars} config={s.config}
            justAdded={p.replay > 0} source={sourceOf(p.result, s)} onSelect={p.onSelect}
            onLabelClick={p.small ? undefined : (label, x, y) => { setEditLabel({ label, x, y }); p.onPulse(`rename:${label}`); }} />
        )}
        {editLabel && (
          <input className="cs-card__label-input" aria-label={`Show ${editLabel.label} as`} autoFocus defaultValue={editLabel.label}
            style={{ left: Math.max(0, editLabel.x - 90), top: editLabel.y - 12 }}
            onBlur={(e) => { const v = e.target.value.trim(); setEditLabel(null); if (v && v !== editLabel.label) p.onRename(editLabel.label, v); }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); if (e.key === "Escape") { e.preventDefault(); setEditLabel(null); } }} />
        )}
        {p.dropHint && !p.small && <span className="cs-release"><CIcon name="git-fork" size={12} />Release to split bars</span>}
      </div>
    </div>
  );
}
